import React from 'react';
import { Tooltip } from '@mui/material';

import { DataTable, type TableColumn, EditIcon, TrashIcon } from '@/src/ui';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';
import type { StandardDTO } from '../models/standardsDTO';

interface StandardsTableProps {
    standards: StandardDTO[];
    onEdit: (standard: StandardDTO) => void;
    onDelete: (standard: StandardDTO) => void;
}

const columns: TableColumn[] = [
    {
        dataKey: 'name',
        header: 'Name',
        allowSort: true,
        align: 'left',
        headerSx: ReportHeaderStyle,
        cellSx: ReportCellStyle,
    },
    {
        dataKey: 'gradeLevelName',
        header: 'Grade Level',
        allowSort: true,
        align: 'right',
        headerSx: ReportHeaderStyle,
        cellSx: ReportCellStyle,
        formatter: (row) => row.gradeLevelName || row.gradeLevelMasterId,
    },
    {
        dataKey: 'status',
        header: 'Status',
        allowSort: true,
        align: 'right',
        headerSx: ReportHeaderStyle,
        cellSx: ReportCellStyle,
        formatter: (row) => {
            switch (row.status) {
                case 1:
                    return 'Active';
                case 2:
                    return 'Inactive';
                case 3:
                    return 'Deleted';
                default:
                    return row.status;
            }
        },
    },
    {
        dataKey: 'sortOrder',
        header: 'Sort Order',
        allowSort: true,
        align: 'right',
        headerSx: ReportHeaderStyle,
        cellSx: ReportCellStyle,
    },
    {
        dataKey: 'standardDomainMasterId',
        header: 'Domain',
        allowSort: true,
        align: 'right',
        headerSx: ReportHeaderStyle,
        cellSx: ReportCellStyle,
    },
    {
        dataKey: 'actions',
        header: 'Actions',
        align: 'center',
        headerSx: ReportHeaderStyle,
        cellSx: ReportCellStyle,
        formatter: (row) => (
            <div className="flex justify-center">
                <Tooltip title="Edit" arrow>
                    <button
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                        onClick={() => row.onEdit(row)}
                    >
                        <span role="img" aria-label="edit">
                            <EditIcon />
                        </span>
                    </button>
                </Tooltip>
                <Tooltip title="Delete" arrow>
                    <button
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                        onClick={() => row.onDelete(row)}
                    >
                        <span role="img" aria-label="delete">
                            <TrashIcon />
                        </span>
                    </button>
                </Tooltip>
            </div>
        ),
        allowSort: false,
    },
];

const StandardsTable: React.FC<StandardsTableProps> = ({ standards, onEdit, onDelete }) => {
    const dataWithActions = standards.map((s) => ({ ...s, onEdit, onDelete }));
    return (
        <DataTable
            data={dataWithActions}
            columns={columns}
            tableName="standards-table"
            alternatingRowColor
            defaultSortColumn="name"
            defaultSortDirection="asc"
            emptyMessage="No standards found."
        />
    );
};

export default StandardsTable;
